import { useEffect, useRef } from "react";
import { useCodebaseStore } from "@/store/zustand/codebaseStore";
import { useCanvasId } from "@/utils/utils";
import { useSession } from "@clerk/clerk-react";
import axios from "axios";
import { useTypedSelector } from "@/store/store";
import { TAILWIND_PROPERTIES } from "@/tailwind/tailwindUtils";
import type { ClassType } from "@/tailwind/tailwindUtils";
import type { Database } from "@/utils/supabase.types";

export const useTailwindClasses = () => {
  const canvasId = useCanvasId();
  const { session } = useSession();

  const projectLoadingProgress:
    | Database["public"]["Tables"]["project_loading_progress"]["Row"]
    | null = useTypedSelector(
    (state) => state.supabaseStore.projectLoadingProgress,
  );

  const fetchedRef = useRef(false);

  useEffect(() => {
    if (fetchedRef.current) return;

    const fetchTailwindClasses = async () => {
      if (!session || !canvasId) return;

      // Have to wait for the container to be launched and dev server to be started
      // so we can run the generation
      if (
        !projectLoadingProgress?.launched_container ||
        !projectLoadingProgress?.started_dev_server
      ) {
        return;
      }

      fetchedRef.current = true;

      try {
        const supabaseJWT = await session?.getToken();
        const response = await axios.get(
          `${
            import.meta.env.VITE_BACKEND_BASE_URL
          }/canvases/${canvasId}/tailwind/classes`,
          {
            headers: {
              Authorization: `Bearer ${supabaseJWT}`,
            },
          },
        );

        const currentClasses = useCodebaseStore.getState().tailwindClasses;
        const newClasses = response.data;

        // Preserve existing design token classes using the isDesignToken marker
        const designTokenClasses = currentClasses.filter(
          (cls) => cls.isDesignToken === true,
        );

        // Create a Set of new class names for efficient lookup
        const newClassNames = new Set(
          newClasses.map((cls: ClassType) => cls.tailwind),
        );

        // Filter out design token classes that might conflict with new ones
        const uniqueDesignTokenClasses = designTokenClasses.filter(
          (cls) => !newClassNames.has(cls.tailwind),
        );

        // Combine new classes with preserved design token classes
        const mergedClasses = [...newClasses, ...uniqueDesignTokenClasses];

        useCodebaseStore.getState().setTailwindClasses(mergedClasses);
      } catch (error) {
        console.error("Error fetching Tailwind classes:", error);
      }
    };

    fetchTailwindClasses();
  }, [
    canvasId,
    projectLoadingProgress?.launched_container,
    projectLoadingProgress?.started_dev_server,
    session,
  ]);

  // Static backup
  useEffect(() => {
    useCodebaseStore.getState().setTailwindClasses(TAILWIND_PROPERTIES);
  }, []);
};
